import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  QueryList,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { SvgIconComponent } from '../../../svg-icon/svg-icon.component';
import { BackButtonComponent } from '../../../back-button/back-button.component';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormArray,
} from '@angular/forms';
import { ApiService } from '../../../../services/api.service';
import { LoadingService } from '../../../../services/loading.service';
import { ToasterService } from '../../../../services/toaster.service';
import { SharedModule } from '../../../../modules/shared/shared.module';
import { ActivatedRoute, Router } from '@angular/router';
import { Customer } from '../../../../models/customer-master.interface';
import { AutocompleteDropdownComponent } from '../../../autocomplete-dropdown/autocomplete-dropdown.component';
import { ToggleComponent } from '../../../common/toggle/toggle.component';

import {
  MatExpansionModule,
  MatExpansionPanel,
} from '@angular/material/expansion';
import { MatTabsModule } from '@angular/material/tabs';
import { HorizontalTabsComponent } from '../../../horizontal-tabs/horizontal-tabs.component';
import { PageCloseButtonComponent } from "../../../common/close-button/page-close-button.component";
import { CustomAccordionComponent } from '../../../custom-accordion/custom-accordion.component';
import { MultiSelectComponent } from '../../../select/multi-select.component';
import { CycleType, PAN_NUMBER_REGEX } from '../../../../constants/app-constants';
import { ModalComponent } from '../../../modal/modal.component';
import { CONTACT_ACTIONS, ContactActions, ContactService } from '../../../../services/masters/contact.service';
import { ContactInformation } from '../../../../models/masters/contact-information.interface';
import { ContactDialogComponent } from '../../contact-information/contact-dialog/contact-dialog.component';
import { async, firstValueFrom, take } from 'rxjs';
import { InfoCardBannerComponent } from '../../../info-card-banner/info-card-banner.component';
import { CodeFormatDirective } from '../../../../directives/code-input.directive';
import { UppercaseInputDirective } from '../../../../directives/upper-case-input.directive';

import contactConfig from "./config/customer-contact-config.json";
import { DynamicForm } from '../../../../models/dynamic-form';
import { NoDataComponent } from '../../../no-data/no-data.component';
import { CustomValidators } from '../../../../validators/custom-validators';

@Component({
  selector: 'app-manage-customer',
  standalone: true,
  imports: [
    SharedModule,
    SvgIconComponent,
    BackButtonComponent,
    AutocompleteDropdownComponent,
    MatExpansionModule,
    MatTabsModule,
    HorizontalTabsComponent,
    ToggleComponent,
    PageCloseButtonComponent,
    CustomAccordionComponent,
    MultiSelectComponent,
    InfoCardBannerComponent,
    CodeFormatDirective,
    UppercaseInputDirective,
    NoDataComponent
],
  templateUrl: './manage-customer.component.html',
  styleUrl: './manage-customer.component.scss',
})
export class ManageCustomerComponent {
  @Input() customerEditingEnabled: boolean = false;
  @Input() customerId!: string;
  @Input() customerObj!: Customer;

  @ViewChildren('formField') formFields!: QueryList<ElementRef>;
  @ViewChildren(MatExpansionPanel) expansionPanels!: QueryList<MatExpansionPanel>;



  readOnlyMode: boolean = true;
  customerForm!: FormGroup;
  validatePanNumber: boolean = true;
  customerTypes = [
    { name: 'Freight Forwarder', value: 'FREIGHT_FORWARDER', checked: false },
    { name: 'Consignee', value: 'CONSIGNEE', checked: false },
    { name: 'Line Agent', value: 'LINE_AGENT', checked: false },
    { name: 'Line', value: 'LINE', checked: false },
    { name: 'CHA', value: 'CHA', checked: false },
    { name: 'Transporter', value: 'TRANSPORTER', checked: false },
    { name: 'Shipper', value: 'SHIPPER', checked: false },
    { name: 'Exporter', value: 'EXPORTER', checked: false },
    { name: 'Group Company', value: 'GROUP_COMPANY', checked: false },
    { name: 'Other CFS', value: 'OTHER_CFS', checked: false },
  ];
  cycleOptions = Object.values(CycleType);
  selectedTab: string = 'BasicDetails'; // Default tab

  tabList = [
    { id: 'BasicDetails', label: 'Basic Details' },
    { id: 'ContactInformation', label: 'Contact Information' },
  ];

  contactFormConfig!: DynamicForm;
  customerDataRes?: Customer;
  contactList: ContactInformation[] = [];
  contactActions: { icon: string; name: string; class: string }[] = [];

  selectedOption: string = 'pan';
  accDisabled: boolean = true;
  disableAction: boolean = true;

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private toasterService: ToasterService,
    private loadingService: LoadingService,
    private viewContainerRef: ViewContainerRef,
    private router: Router,
    private route: ActivatedRoute,
    private contactService: ContactService,
    private cdr: ChangeDetectorRef
  ) {
    this.initForm();
    this.contactFormConfig = contactConfig as DynamicForm;
  }

  ngOnInit() {
    this.customerId = this.route.snapshot.paramMap.get('customerId') || '';
    this.validatePanNumber = !this.customerId;

    if (this.customerId) {
      this.configureActionMode();
      this.fetchAndPopulateCustomerData();
    } else {
      this.readOnlyMode = false;
      this.customerEditingEnabled = false;
      this.accDisabled = true;
    }
    this.contactActions = this.contactService.getContactActions(this.readOnlyMode);
  }

  private initForm(): void {
    this.customerForm = this.fb.group({
      panNumber: ['', [Validators.required, CustomValidators.pattern(PAN_NUMBER_REGEX)]],
      code: ['', [Validators.required, Validators.maxLength(10)]],
      name: ['', Validators.required],
      parentCustomerId: [''],
      sisterCustomerId: [''],
      customerType: [null, Validators.required],
      nominated: [{value: false, disabled: true}],
      contactInformation: null,
      salesPerson: [null],
      tanNumber: [null],
      importerExporterCode: [null],
      pdaEnabled: [false],
      pdaCreditLimit: [{ value: 0, disabled: true }],
      pdaEnabledCycles: [{value: null, disabled: true}],
      creditEnabled: [false],
      creditEnabledCycles: [{value: null, disabled: true}],
      creditDays: [{ value: 0, disabled: true }],
      creditLimit: [{ value: 0, disabled: true }],

      active: [true],
    });
  }

  private configureActionMode(): void {
    const isEditMode = this.route.snapshot.url[0].path === 'edit';
    this.customerEditingEnabled = isEditMode;
    this.readOnlyMode = !isEditMode;
    this.accDisabled = false;
    this.disableAction = false;
    if (!isEditMode) {
      this.customerForm.disable();
    }
  }

  private async fetchAndPopulateCustomerData(): Promise<void> {
    this.loadingService.show();
    try {
      const res: any = await firstValueFrom(this.api.getCustomer(this.customerId));
      this.customerDataRes = res.data;
      if (this.customerDataRes) {
        this.populateCustomerForm(this.customerDataRes);
      }
    } catch (err) {
      console.error('Error loading customer:', err);
    } finally {
      this.loadingService.hide();
    }
  }

  private populateCustomerForm(data: Customer): void {
    this.contactList = data.contactInformation ?? [];
    this.contactList.forEach((contact, index) => contact.contactName = contact.contactName ?? 'Contact ' + (index + 1));
    this.customerForm.patchValue({
      ...data,
      sisterCustomerId: data.sisterCustomerId ? {key: data.sisterCustomerId, value: data.sisterCustomerName} : null,
      parentCustomerId: data.parentCustomerId ? {key: data.parentCustomerId, value: data.parentCustomerName} : null,
      salesPerson: data.salesPerson ? {key: data.salesPerson.id, value: data.salesPerson.name} : null,
      pdaEnabledCycles: data.pdaEnabledCycles?.map((cycle: any) => cycle.id),
      creditEnabledCycles: data.creditEnabledCycles?.map((cycle: any) => cycle.id),
      customerType: data.customerType?.map((type: any) => type.id),
    });
    this.onPdaEnabledChange(data.pdaEnabled);
    this.onCreditEnabledChange(data.creditEnabled);
    this.onCustomerTypeChange(data.customerType);
  }


  selectTab(tabId: string) {
    this.selectedTab = tabId;
  }

  changeLabel(option: string) {
    this.selectedOption = option;
    this.customerForm.get('panNumber')?.clearValidators();
    if (option === 'pan') {
      this.customerForm.get('panNumber')?.addValidators([Validators.required, CustomValidators.pattern(PAN_NUMBER_REGEX)]);
    } else {
      this.customerForm.get('panNumber')?.addValidators([Validators.required]);
    }
  }

  getTabContainingElement(element: HTMLElement): string | null {
    const tabMappings: { [key: string]: string } = {
      'basic-details-section': 'BasicDetails',
      'contact-information-section': 'ContactInformation',
    };

    // console.log('Checking invalid field inside:', element);

    for (const sectionId in tabMappings) {
      const sectionElement = element.closest(`#${sectionId}`);
      // console.log(`Checking section: #${sectionId}`, sectionElement);

      if (sectionElement) {
        // console.log(`Invalid field found inside: ${sectionId}`);
        return tabMappings[sectionId];
      }
    }

    console.warn('No matching tab found for invalid field');
    return null;
  }

  scrollToFirstInvalidControl() {
    setTimeout(() => {
      // Get all invalid form controls
      const invalidControls = this.formFields.filter((element) => {
        return !element.nativeElement.validity?.valid;
      });
      console.log(invalidControls)

      if (invalidControls.length > 0) {
        const firstInvalidControl = invalidControls[0];
        const invalidControlElement = firstInvalidControl.nativeElement;

        // Find the tab that contains the invalid field
        const invalidTab = this.getTabContainingElement(invalidControlElement);

        if (invalidTab && this.selectedTab !== invalidTab) {
          this.selectedTab = invalidTab; // Switch to the correct tab

          setTimeout(() => {
            this.scrollToInvalidField(firstInvalidControl.nativeElement);
          }, 500);
        } else {
          // If already on the correct tab, scroll immediately
          this.scrollToInvalidField(firstInvalidControl.nativeElement);
        }
      }
    }, 100);
  }

  scrollToInvalidField(element: HTMLElement) {
    // Expand the accordion if the invalid field is inside it
    this.expansionPanels.forEach((panel) => {
      if (panel._body.nativeElement.contains(element)) {
        panel.expanded = true;
        // console.log(element);
      }
    });

    // Smooth scroll to the invalid field
    setTimeout(() => {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',


      });
      element.focus();
    }, 300);
  }

  handleContactAction(index: number, action: string) {
    if (action === 'edit') {
      this.createModalForContactConfiguration(CONTACT_ACTIONS.EDIT, index);
    } else if (action === 'view') {
      this.createModalForContactConfiguration(CONTACT_ACTIONS.VIEW, index);
    } else if (action === 'clone') {
      this.createModalForContactConfiguration(CONTACT_ACTIONS.CLONE, index);
    }
  }

  addContact() {
    this.createModalForContactConfiguration(CONTACT_ACTIONS.ADD);
  }

  private createModalForContactConfiguration(action: ContactActions, index?: number) {
    const modalComponentComponentRef = this.viewContainerRef.createComponent(ModalComponent);
    modalComponentComponentRef.instance.width = "80vw";
    modalComponentComponentRef.instance.height = "90vh";
    modalComponentComponentRef.instance.title = this.getModalTitle(action);

    const componentRef = modalComponentComponentRef.instance.loadComponent(ContactDialogComponent);
    componentRef.instance.action = action;
    componentRef.instance.contactInformation = index !== undefined ? this.contactList[index] : null;
    componentRef.instance.contactFormConfig = this.contactFormConfig;

    modalComponentComponentRef.instance.close.subscribe((res: any) => {
      if (res) {
        const isDefaultContact = res.defaultContact ?? false;
        if (action === CONTACT_ACTIONS.ADD) {
          this.contactList.push(res);
          index = this.contactList.length - 1;
        } else if (action === CONTACT_ACTIONS.EDIT && index !== undefined) {
          this.contactList[index] = res;
        } else if (action === CONTACT_ACTIONS.CLONE) {
          this.contactList.push(res);
          index = this.contactList.length - 1;
        }
        this.onDefaultContactChange(isDefaultContact, index!);
      }
      modalComponentComponentRef.destroy();
    });
  }

  private getModalTitle(action: ContactActions): string {
    switch (action) {
      case CONTACT_ACTIONS.ADD: return 'Add Contact';
      case CONTACT_ACTIONS.EDIT: return 'Edit Contact';
      case CONTACT_ACTIONS.VIEW: return 'View Contact';
      default: return 'Contact';
    }
  }

  async submitCustomerForm() {

    this.customerForm.markAllAsTouched();

    if (this.customerForm.valid) {
      const formValue = this.customerForm.value;
      const customerData: Customer = {
        ...formValue,
        nominated: formValue.nominated ?? false,
        contactInformation: this.contactList,
      };

      if (this.customerEditingEnabled) {
        this.updateCustomer(customerData, this.customerId);
      } else {
        this.addCustomer(customerData);
      }
    } else {
      console.log('invalid form');
      this.scrollToFirstInvalidControl(); //scroll having issue
    }
  }

  addCustomer(customerData: Customer) {
    this.loadingService.show();

    this.api.addCustomer(customerData).subscribe({
      next: (res) => {
        this.toasterService.success(
          `Customer ${customerData.name} has been added !`
        );
        this.loadingService.hide();
        this.goBack();
      },
      error: (err) => {
        this.toasterService.error(err.error.errorDesc);
        this.loadingService.hide();
      },
    });
  }

  updateCustomer(customerData: Customer, customerId: string) {
    this.loadingService.show();
    this.api.updateCustomer(customerData, customerId).subscribe({
      next: (res) => {
        console.log(res);
        this.toasterService.success(
          `Customer ${customerData.name} has been updated !`
        );
        this.loadingService.hide();
        this.goBack();
      },
      error: (err) => {
        this.toasterService.error(err.error.errorDesc);
        console.log(err);
        this.loadingService.hide();
      },
    });
  }

  onPanNumberChange(event: any) {
    const panControl = this.customerForm.get('panNumber');
    const panNumber = event.target.value;

    if (this.customerDataRes?.panNumber === panNumber) {
      this.disableAction = false;
      this.validatePanNumber = false;
      return;
    }

    this.disableAction = true;

    this.validatePanNumber = panNumber && (panControl?.valid ?? false);
  }

  handleValueChange(event: {controlName: string, result: any}, index: number) {
    if (event.controlName === 'defaultContact') {
      this.onDefaultContactChange(event.result, index);
    }
  }

  onDefaultContactChange(state: boolean, i: number) {
    if (state) {
      this.contactList.forEach((contact, index) => {
        contact.defaultContact = index === i ? state : false;
      });
    }
  }

  onCustomerTypeChange(event: any[]) {
    if (this.readOnlyMode) return;
    const hasShippingLine = event.some((type: any) => type.value === 'LINE' || type.id === 'LINE');
    if (hasShippingLine) {
      this.customerForm.get('nominated')?.enable();
    } else {
      this.customerForm.get('nominated')?.disable();
      this.customerForm.get('nominated')?.setValue(false);
    }
  }

  onPdaEnabledChange(active: boolean) {
    if (this.readOnlyMode) return;
    if (active) {
      this.customerForm.get('pdaEnabledCycles')?.enable();
      this.customerForm.get('pdaCreditLimit')?.enable();
    } else {
      this.customerForm.get('pdaEnabledCycles')?.disable();
      this.customerForm.get('pdaCreditLimit')?.disable();
      this.customerForm.get('pdaCreditLimit')?.setValue(0);
    }
  }

  onCreditEnabledChange(active: boolean) {
    if (this.readOnlyMode) return;
    if (active) {
      this.customerForm.get('creditEnabledCycles')?.enable();
      this.customerForm.get('creditDays')?.enable();
      this.customerForm.get('creditLimit')?.enable();
    } else {
      this.customerForm.get('creditEnabledCycles')?.disable();
      this.customerForm.get('creditDays')?.disable();
      this.customerForm.get('creditLimit')?.disable();
      this.customerForm.get('creditDays')?.setValue(0);
      this.customerForm.get('creditLimit')?.setValue(0);
    }
  }

  goBack() {
    this.router.navigateByUrl('/manage-masters/customers');
  }

  async checkIfPanNumberIsAllowed() {
    const panFormControl = this.customerForm.get('panNumber');
    const panNumber = panFormControl?.value;
    panFormControl?.markAsTouched();
    if (!panFormControl?.valid) {
      return;
    }
    if (!panNumber) {
      this.accDisabled = true;
      return;
    }
    try {
      const isValid = await this.checkPanNumberValidity(panNumber);

      if (isValid) {
        this.accDisabled = false;
        this.disableAction = false;
        this.validatePanNumber = false;
      } else {
        this.accDisabled = true;
        this.disableAction = true;
        this.toasterService.error('Customer with this PAN Number already exists');
      }
    } catch (error: any) {
      this.toasterService.error(error?.error?.errorDesc || 'PAN validation failed');
      this.accDisabled = true;
    }
  }

  private async checkPanNumberValidity(panNumber: string): Promise<boolean> {
    const response = await firstValueFrom(this.api.checkPanNumberValidity(panNumber));
    return !!response.data;
  }

  getFormattedContactSummary(i: number): string[] {
    return Array.of(this.contactService.getFormattedContactSummary(this.contactList[i]));
  }

  getContactBadges(i: number): { label: string; class: string }[] {
    return this.contactService.getContactBadges(this.contactList[i]);
  }
}
